import * as React from 'react'
import { twMerge } from 'tailwind-merge'

import { Dialog as DialogPrimitive } from 'radix-ui'

import { MODAL_HEIGHT_MAP, MODAL_WIDTH_MAP, type ModalHeight, type ModalWidth } from './constants'

export type ModalContainerProps = {
  /** Max width preset for the dialog panel. Default: `'md'`. */
  width?: ModalWidth
  /** Height preset for the dialog shell (fixed / max-height). Default: `'md'`. */
  height?: ModalHeight
  /** Raise overlay/content z-index so this dialog can stack above another. Default: `false`. */
  stacked?: boolean
  /** Block Escape and outside-pointer dismiss while open. Default: `false`. */
  preventDismiss?: boolean
  /** Additional classes for the dialog panel. */
  className?: string
  /** Forwarded to the dialog Content element. Set by the consuming component (Modal/TabbedModal) so the DS Inspector can identify which one is rendered. */
  'data-ds-component'?: string
  /**
   * Forwarded to the dialog Content element. Pass `undefined` explicitly (not just omit) to suppress
   * Radix's auto-generated id when no `Dialog.Description` is rendered — otherwise Radix warns
   * "Missing Description or aria-describedby" in the console. Leave unset when a description *is*
   * rendered so Radix's own id linkage keeps working.
   */
  'aria-describedby'?: string
}

/**
 * Portal, overlay, and Radix dialog content surface — shared by {@link ModalContent} and {@link TabbedModal}.
 * Owns viewport sizing, height presets, and the outer flex column layout.
 */
export function ModalContainer({
  width = 'md',
  height = 'md',
  stacked = false,
  preventDismiss = false,
  className,
  children,
  ...props
}: React.PropsWithChildren<ModalContainerProps>) {
  const contentRef = React.useRef<HTMLDivElement>(null)

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={twMerge('fixed inset-0 bg-brand-neutral-900 opacity-50 z-[999] data-[state=open]:animate-overlayShow', stacked && 'z-[1001]')}
      />
      <DialogPrimitive.Content
        {...props}
        ref={contentRef}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          contentRef.current?.focus()
        }}
        // `max-h-[85dvh]` applies at every breakpoint so the centered dialog never exceeds the
        // viewport on short screens. The `max-*` height presets embed the same 85dvh clamp via
        // `min()`, so a preset caps size on tall screens while the viewport still wins when it is
        // shorter. Do not add `sm:max-h-none` back — it let the modal grow past short desktop
        // viewports and clip its own content.
        className={twMerge(
          'fixed z-[1000] left-1/2 top-1/2 flex min-h-0 w-[calc(100%-2rem)] max-h-[85dvh] flex-col -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface-dialog shadow-[var(--shadow-6)] focus:outline-hidden data-[state=open]:animate-contentShow',
          stacked && 'z-[1002]',
          MODAL_WIDTH_MAP[width],
          MODAL_HEIGHT_MAP[height],
          className
        )}
        onEscapeKeyDown={(e) => {
          if (preventDismiss) e.preventDefault()
        }}
        onPointerDownOutside={(e) => {
          if (preventDismiss) e.preventDefault()
        }}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}
